import { createFileRoute, notFound, redirect, useLoaderData } from "@tanstack/react-router";
import { ContentRenderer } from "~/components/ContentRenderer";
import { getCmsPageContent } from "~/lib/server-functions/get-cms-page-content";
import { getBaseUrl } from "~/lib/utils";
import { parseLegacySearchPathSegments } from "~/lib/utils/legacy-search-url";
import { withLocale } from "~/lib/utils/with-locale";
import { seo } from "~/utils/seo";

// Known advertiser routes that should not be matched by this catch-all
const advertiserRoutes = [
  "/inquiries",
  "/locations",
  "/user",
  "/add-location",
  "/edit-location",
];

export const Route = createFileRoute("/__main/$locale/$")({
  beforeLoad: async ({ location, params }) => {
    // If we're on the admin subdomain and the path matches an advertiser route,
    // this catch-all should not match - let the advertiser routes handle it
    if (
      typeof window !== "undefined" &&
      window.location.hostname.startsWith(import.meta.env.VITE_ADMIN_HOST) &&
      advertiserRoutes.includes(location.pathname)
    ) {
      throw notFound();
    }

    const pathWithoutLocale = location.pathname.replace(
      new RegExp(`^/${params.locale}(/|$)`),
      "",
    );
    const segments = pathWithoutLocale.split("/").filter(Boolean);
    const legacySearchPrefill = parseLegacySearchPathSegments(segments);
    if (legacySearchPrefill) {
      throw redirect({
        to: withLocale("/search", params.locale),
        search: legacySearchPrefill,
      });
    }
  },
  component: RouteComponent,
  loader: async ({ location, params }) => {
    // Strip locale prefix for CMS slug (e.g. /de/impressum -> /impressum)
    const slug = location.pathname.replace(new RegExp(`^/${params.locale}`), "") || "/";
    try {
      const result = await getCmsPageContent({ data: { slug } });
      // If content is empty, treat as not found
      if (!result.content || result.content.length === 0) {
        throw notFound();
      }
      return result;
    } catch (error: any) {
      // Re-throw notFound() errors to ensure they're handled correctly
      // This ensures both direct navigation and link clicks show the NotFound component
      // Server function errors might come through as HTTP responses with status codes
      if (error && typeof error === "object") {
        // Check for NotFoundError by name
        if (error.name === "NotFoundError" || error.constructor?.name === "NotFoundError") {
          throw notFound();
        }
        
        // Check for 404 status code (server function errors might have this)
        if (error.status === 404 || error.statusCode === 404) {
          throw notFound();
        }
        
        // Check error message - TanStack Start serializes notFound() as JSON in the message
        if (error.message && typeof error.message === "string") {
          // Try to parse as JSON (TanStack Start serializes notFound() as {"isNotFound":true})
          try {
            const parsed = JSON.parse(error.message);
            if (parsed && typeof parsed === "object" && parsed.isNotFound === true) {
              throw notFound();
            }
          } catch {
            // Not JSON, check for text patterns
            const msg = error.message.toLowerCase();
            if (msg.includes("not found") || msg.includes("404")) {
              throw notFound();
            }
          }
        }
      }
      
      // Re-throw other errors
      throw error;
    }
  },
  head: ({ loaderData, params }) => {
    const slug = loaderData?.slug || "/";
    const canonicalPath = withLocale(slug, params.locale);
    const canonicalUrl = `${getBaseUrl()}${canonicalPath}`;
    const cmsTitle = loaderData?.pageTitle?.trim() || "";
    const title = cmsTitle
      ? `${cmsTitle} | Locationguide24`
      : "Locationguide24";

    return {
      meta: [
        ...seo({
          title,
          url: canonicalUrl,
          siteName: "Locationguide24",
          locale: "de_DE",
        }),
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
});

function RouteComponent() {
  const { content } = useLoaderData({ from: "/__main/$locale/$" });
  return (
    <>
      {content.map((item: any) => (
        <ContentRenderer key={item.uid} item={item} />
      ))}
    </>
  );
}
